/**
 * Blog Post FAQ Schema Extractor
 *
 * Automatically detects FAQ-style content in blog posts and generates
 * FAQPage structured data for rich snippet eligibility.
 *
 * Detection Rules:
 * - H2/H3 headers that are questions (end with ?)
 * - Q&A formatted content (Q: ... A: ...)
 * - Numbered or bulleted FAQ sections
 * - Headers starting with common question words (What, Why, How, When, Where, Can, Do, Is)
 *
 * Critical SEO Requirements:
 * - All text must be plain text (NO HTML/Markdown)
 * - Minimum 2 FAQ items for valid schema
 * - Questions must be distinct
 */

import type { BlogPost } from '../blog/types';

export interface ExtractedFAQ {
  question: string;
  answer: string;
}

/**
 * Strip markdown and HTML formatting from text
 */
function stripFormatting(text: string): string {
  return text
    // Remove markdown links: [text](url) -> text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    // Remove bold/italic: **text** or *text* -> text
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    // Remove inline code: `code` -> code
    .replace(/`([^`]+)`/g, '$1')
    // Remove code blocks
    .replace(/```[\s\S]*?```/g, '')
    // Remove headings markers: ### text -> text
    .replace(/^#{1,6}\s+/gm, '')
    // Remove blockquotes: > text -> text
    .replace(/^>\s+/gm, '')
    // Remove HTML tags
    .replace(/<[^>]*>/g, '')
    // Clean up HTML entities
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    // Clean up extra whitespace
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Check if text is a question (ends with ? or starts with question words)
 */
function isQuestion(text: string): boolean {
  const trimmed = text.trim();

  // Ends with question mark
  if (trimmed.endsWith('?')) {
    return true;
  }

  // Starts with common question words (multilingual support)
  const questionPatterns = [
    // English
    /^(what|why|how|when|where|who|which|can|do|does|is|are|will|should|could|would|have|has)\s+/i,
    // German
    /^(was|warum|wie|wann|wo|wer|welche|kann|können|ist|sind|wird|soll|sollte|hätte|hat|haben)\s+/i,
    // Portuguese
    /^(o que|por que|como|quando|onde|quem|qual|pode|é|são|será|deve|deveria|tem)\s+/i,
    // Spanish
    /^(qué|por qué|cómo|cuándo|dónde|quién|cuál|puede|es|son|será|debe|debería|tiene)\s+/i,
    // Malay
    /^(apa|kenapa|mengapa|bagaimana|bila|di mana|siapa|boleh|adakah|akan)\s+/i,
  ];

  return questionPatterns.some(pattern => pattern.test(trimmed));
}

/**
 * Extract the answer text following a question header
 */
function extractAnswer(content: string, questionStart: number, questionEnd: number): string {
  // Find the next header (##, ###, etc.) or end of content
  const restContent = content.slice(questionEnd);
  const nextHeaderMatch = restContent.match(/\n#{1,6}\s+/);

  let answerText: string;
  if (nextHeaderMatch && nextHeaderMatch.index !== undefined) {
    answerText = restContent.slice(0, nextHeaderMatch.index);
  } else {
    answerText = restContent;
  }

  // Clean up the answer
  answerText = stripFormatting(answerText);

  // Limit answer length (Google recommends under 320 characters for snippets, but allows more)
  if (answerText.length > 500) {
    // Try to cut at a sentence boundary
    const sentences = answerText.match(/[^.!?]+[.!?]+/g) || [];
    let truncated = '';
    for (const sentence of sentences) {
      if (truncated.length + sentence.length <= 500) {
        truncated += sentence;
      } else {
        break;
      }
    }
    answerText = truncated || answerText.slice(0, 497) + '...';
  }

  return answerText.trim();
}

/**
 * Extract FAQ items from markdown content
 * Strategy 1: H2/H3 headers that are questions followed by content
 */
function extractHeaderFAQs(content: string): ExtractedFAQ[] {
  const faqs: ExtractedFAQ[] = [];
  const seenQuestions = new Set<string>();

  // Match H2 and H3 headers
  const headerPattern = /^(#{2,3})\s+(.+)$/gm;
  let match: RegExpExecArray | null;

  while ((match = headerPattern.exec(content)) !== null) {
    const headerText = match[2].trim();

    if (isQuestion(headerText)) {
      const cleanQuestion = stripFormatting(headerText);

      // Skip duplicate questions
      if (seenQuestions.has(cleanQuestion.toLowerCase())) {
        continue;
      }

      const answer = extractAnswer(content, match.index, match.index + match[0].length);

      if (answer && answer.length >= 50) {
        faqs.push({
          question: cleanQuestion,
          answer: answer,
        });
        seenQuestions.add(cleanQuestion.toLowerCase());
      }
    }
  }

  return faqs;
}

/**
 * Extract FAQ items from Q&A formatted content
 * Strategy 2: Q: ... / A: ... patterns
 */
function extractQAFormat(content: string): ExtractedFAQ[] {
  const faqs: ExtractedFAQ[] = [];
  const seenQuestions = new Set<string>();

  // Match Q: question / A: answer patterns
  const qaPattern = /(?:^|\n)\s*(?:\*\*)?Q(?:uestion)?[:\s]+(?:\*\*)?(.+?)(?:\*\*)?\s*\n\s*(?:\*\*)?A(?:nswer)?[:\s]+(?:\*\*)?(.+?)(?=\n\s*(?:\*\*)?Q(?:uestion)?[:\s]|\n\n|\$)/gis;

  let match: RegExpExecArray | null;
  while ((match = qaPattern.exec(content)) !== null) {
    const question = stripFormatting(match[1]);
    const answer = stripFormatting(match[2]);

    if (!seenQuestions.has(question.toLowerCase()) && answer.length >= 30) {
      faqs.push({ question, answer });
      seenQuestions.add(question.toLowerCase());
    }
  }

  return faqs;
}

/**
 * Extract FAQ items from "FAQ" or "Frequently Asked Questions" sections
 * Strategy 3: Look for dedicated FAQ sections
 */
function extractFAQSection(content: string): ExtractedFAQ[] {
  const faqs: ExtractedFAQ[] = [];

  // Find FAQ section
  const faqSectionPattern = /#{1,3}\s*(?:FAQ|Frequently Asked Questions|Häufige Fragen|Perguntas Frequentes|Preguntas Frecuentes|Soalan Lazim).*?\n([\s\S]+?)(?=\n#{1,2}\s+[^#]|$)/gi;
  const sectionMatch = faqSectionPattern.exec(content);

  if (sectionMatch) {
    const sectionContent = sectionMatch[1];

    // Look for numbered or bulleted items within the section
    const itemPattern = /(?:^\d+\.\s+|^[-*]\s+)(?:\*\*)?(.+?\?)(?:\*\*)?\s*\n+(.+?)(?=\n(?:\d+\.\s+|[-*]\s+|\n|$))/gm;

    let itemMatch: RegExpExecArray | null;
    while ((itemMatch = itemPattern.exec(sectionContent)) !== null) {
      const question = stripFormatting(itemMatch[1]);
      const answer = stripFormatting(itemMatch[2]);

      if (question && answer && answer.length >= 30) {
        faqs.push({ question, answer });
      }
    }
  }

  return faqs;
}

/**
 * Detect if a blog post has FAQ-style content worth extracting
 */
export function hasFAQContent(post: BlogPost): boolean {
  const content = post.content;

  // Check for explicit FAQ section
  if (/#{1,3}\s*(?:FAQ|Frequently Asked Questions|Häufige Fragen|Perguntas Frequentes|Preguntas Frecuentes|Soalan Lazim)/i.test(content)) {
    return true;
  }

  // Check for Q&A format
  if (/(?:^|\n)\s*Q(?:uestion)?[:\s]+/im.test(content)) {
    return true;
  }

  // Check for multiple question headers
  const questionHeaders = content.match(/^#{2,3}\s+.+\?$/gm);
  if (questionHeaders && questionHeaders.length >= 2) {
    return true;
  }

  return false;
}

/**
 * Extract FAQs from a blog post's markdown content
 */
export function extractFAQsFromPost(post: BlogPost): ExtractedFAQ[] {
  const content = post.content;
  const allFaqs: ExtractedFAQ[] = [];
  const seenQuestions = new Set<string>();

  // Strategy 1: Header-based FAQs (most common)
  const headerFaqs = extractHeaderFAQs(content);
  for (const faq of headerFaqs) {
    if (!seenQuestions.has(faq.question.toLowerCase())) {
      allFaqs.push(faq);
      seenQuestions.add(faq.question.toLowerCase());
    }
  }

  // Strategy 2: Q&A format FAQs
  const qaFaqs = extractQAFormat(content);
  for (const faq of qaFaqs) {
    if (!seenQuestions.has(faq.question.toLowerCase())) {
      allFaqs.push(faq);
      seenQuestions.add(faq.question.toLowerCase());
    }
  }

  // Strategy 3: Dedicated FAQ section
  const sectionFaqs = extractFAQSection(content);
  for (const faq of sectionFaqs) {
    if (!seenQuestions.has(faq.question.toLowerCase())) {
      allFaqs.push(faq);
      seenQuestions.add(faq.question.toLowerCase());
    }
  }

  // Return only if we have at least 2 valid FAQs
  return allFaqs.length >= 2 ? allFaqs.slice(0, 10) : [];
}

/**
 * Generate FAQPage schema for a blog post
 */
export function generateBlogFAQSchema(post: BlogPost, canonicalUrl: string): Record<string, unknown> | null {
  const faqs = extractFAQsFromPost(post);

  if (faqs.length < 2) {
    return null;
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

/**
 * Generate FAQ schema script tag for SSR injection
 */
export function generateBlogFAQSchemaScript(post: BlogPost, canonicalUrl: string): string {
  const schema = generateBlogFAQSchema(post, canonicalUrl);

  if (!schema) {
    return '';
  }

  return `<script type="application/ld+json" data-schema-type="blog-faq">${JSON.stringify(schema)}</script>`;
}

/**
 * Inject FAQ schema into document head (client-side)
 */
export function injectBlogFAQSchema(schema: Record<string, unknown>): () => void {
  // Guard against SSR
  if (typeof window === 'undefined') {
    return () => {};
  }

  // Remove any existing blog FAQ schema
  const existingSchema = document.querySelector('script[data-schema-type="blog-faq"]');
  if (existingSchema) {
    existingSchema.remove();
  }

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-schema-type', 'blog-faq');
  script.textContent = JSON.stringify(schema);
  document.head.appendChild(script);

  // Return cleanup function
  return () => {
    if (document.head.contains(script)) {
      document.head.removeChild(script);
    }
  };
}
